import { INCH_TO_PX } from '../constant';
import { DoorType, DoorSwing } from '../config';

export class CanvasUtils {
    static drawCircle(ctx, point, radius, color = 'blue', opacity = 1) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.globalAlpha = opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.closePath();
    }

    static drawRectangle(
        ctx,
        centerPoint,
        width,
        height,
        color = 'blue',
        opacity = 1,
        isStroke = false,
    ) {
        ctx.beginPath();
        ctx.rect(
            centerPoint.x - width / 2,
            centerPoint.y - height / 2,
            width,
            height,
        );
        if (isStroke) {
            ctx.strokeStyle = color;
            ctx.stroke();
        } else {
            ctx.globalAlpha = opacity;
            ctx.fillStyle = color;
            ctx.fill();
        }
        ctx.closePath();
    }

    static drawRectangleForObstacles(
        ctx,
        centerPoint,
        width,
        height,
        angle,
        color = 'blue',
        opacity = 1,
    ) {
        ctx.save();
        ctx.translate(centerPoint.x, centerPoint.y);
        const rad = (angle * Math.PI) / 180;
        ctx.rotate(rad);
        ctx.fillStyle = color;
        ctx.globalAlpha = opacity;
        ctx.fillRect(-width / 2, -height / 2, width, height);
        ctx.restore();
    }

    static drawLines(ctx, points, color, width = 2, scale = 1) {
        ctx.beginPath();
        ctx.moveTo(points[0].x / scale, points[0].y / scale);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x / scale, points[i].y / scale);
        }
        ctx.lineTo(points[0].x / scale, points[0].y / scale); // draw line from last point to first point
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.stroke();
    }

    static drawDoorSwing(
        ctx,
        point1,
        point2,
        openingDirection,
        roomCoordinatesArray,
        color = 'blue',
        type,
        swing,
        opacity = 1,
    ) {
        if (!type) type = DoorType.SINGLE;
        if (type === DoorType.SINGLE) {
            const roomCenter = roomCoordinatesArray.reduce(
                (acc, { x, y }) => ({ x: acc.x + x, y: acc.y + y }),
                { x: 0, y: 0 },
            );
            roomCenter.x /= roomCoordinatesArray.length;
            roomCenter.y /= roomCoordinatesArray.length;

            const isPoint1Left =
                (point1.x - roomCenter.x) * (point2.y - roomCenter.y) -
                    (point2.x - roomCenter.x) * (point1.y - roomCenter.y) <
                0;

            let hingePoint, endPoint;
            if (openingDirection === 'OUTSIDE') {
                if (swing === DoorSwing.LEFT) {
                    hingePoint = isPoint1Left ? point1 : point2;
                    endPoint = isPoint1Left ? point2 : point1;
                } else if (swing === DoorSwing.RIGHT) {
                    hingePoint = isPoint1Left ? point2 : point1;
                    endPoint = isPoint1Left ? point1 : point2;
                } else {
                    ctx.beginPath();
                    ctx.globalAlpha = opacity;
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 7;
                    ctx.moveTo(point1.x, point1.y);
                    ctx.lineTo(point2.x, point2.y);
                    ctx.stroke();
                    return;
                }
            } else if (openingDirection === 'INSIDE') {
                if (swing === DoorSwing.LEFT) {
                    hingePoint = isPoint1Left ? point2 : point1;
                    endPoint = isPoint1Left ? point1 : point2;
                } else if (swing === DoorSwing.RIGHT) {
                    hingePoint = isPoint1Left ? point1 : point2;
                    endPoint = isPoint1Left ? point2 : point1;
                } else {
                    ctx.beginPath();
                    ctx.globalAlpha = opacity;
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 7;
                    ctx.moveTo(point1.x, point1.y);
                    ctx.lineTo(point2.x, point2.y);
                    ctx.stroke();
                    return;
                }
            } else {
                ctx.beginPath();
                ctx.globalAlpha = opacity;
                ctx.strokeStyle = color;
                ctx.lineWidth = 7;
                ctx.moveTo(point1.x, point1.y);
                ctx.lineTo(point2.x, point2.y);
                ctx.stroke();
                return;
            }

            const doorWidth = Math.hypot(
                endPoint.x - hingePoint.x,
                endPoint.y - hingePoint.y,
            );

            const isLeft =
                (hingePoint.x - roomCenter.x) * (endPoint.y - roomCenter.y) -
                    (endPoint.x - roomCenter.x) *
                        (hingePoint.y - roomCenter.y) <
                0;

            const sign = isLeft ? 1 : -1;
            const swingAngle =
                openingDirection === 'OUTSIDE'
                    ? (sign * Math.PI) / 2
                    : (-sign * Math.PI) / 2;

            const startAngle = Math.atan2(
                endPoint.y - hingePoint.y,
                endPoint.x - hingePoint.x,
            );
            const endAngle = startAngle + swingAngle;
            const counterclockwise = swingAngle < 0;

            ctx.beginPath();
            ctx.globalAlpha = opacity;
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.arc(
                hingePoint.x,
                hingePoint.y,
                doorWidth,
                startAngle,
                endAngle,
                counterclockwise,
            );
            ctx.stroke();

            const doorEdge = {
                x: hingePoint.x + Math.cos(endAngle) * doorWidth,
                y: hingePoint.y + Math.sin(endAngle) * doorWidth,
            };

            ctx.beginPath();
            ctx.lineWidth = 7;
            ctx.moveTo(hingePoint.x, hingePoint.y);
            ctx.lineTo(doorEdge.x, doorEdge.y);
            ctx.stroke();
        } else if (type === DoorType.DOUBLE) {
            const roomCenter = roomCoordinatesArray.reduce(
                (acc, { x, y }) => ({ x: acc.x + x, y: acc.y + y }),
                { x: 0, y: 0 },
            );
            roomCenter.x /= roomCoordinatesArray.length;
            roomCenter.y /= roomCoordinatesArray.length;

            const isLeft =
                (point1.x - roomCenter.x) * (point2.y - roomCenter.y) -
                    (point2.x - roomCenter.x) * (point1.y - roomCenter.y) <
                0;

            const totalWidth = Math.hypot(
                point2.x - point1.x,
                point2.y - point1.y,
            );
            const halfWidth = totalWidth / 2;

            const midPoint = {
                x: (point1.x + point2.x) / 2,
                y: (point1.y + point2.y) / 2,
            };

            const drawLeaf = (hingePoint, endPoint, swingDirection) => {
                const baseAngle = Math.atan2(
                    endPoint.y - hingePoint.y,
                    endPoint.x - hingePoint.x,
                );

                // Fixed 90-degree swing (Math.PI / 2 radians)
                const swingAngle =
                    (openingDirection === 'OUTSIDE' ? 1 : -1) *
                    ((swingDirection * Math.PI) / 2) *
                    (isLeft ? 1 : -1);

                const startAngle = baseAngle;
                const endAngle = startAngle + swingAngle;
                const counterclockwise = swingAngle < 0;

                ctx.beginPath();
                ctx.globalAlpha = opacity;
                ctx.strokeStyle = color;
                ctx.lineWidth = 2;
                ctx.arc(
                    hingePoint.x,
                    hingePoint.y,
                    halfWidth,
                    startAngle,
                    endAngle,
                    counterclockwise,
                );
                ctx.stroke();

                const doorEdge = {
                    x: hingePoint.x + Math.cos(endAngle) * halfWidth,
                    y: hingePoint.y + Math.sin(endAngle) * halfWidth,
                };

                ctx.beginPath();
                ctx.lineWidth = 7;
                ctx.moveTo(hingePoint.x, hingePoint.y);
                ctx.lineTo(doorEdge.x, doorEdge.y);
                ctx.stroke();

                ctx.beginPath();
                ctx.fillStyle = color;
                ctx.arc(hingePoint.x, hingePoint.y, 2.5, 0, Math.PI * 2);
                ctx.fill();
            };

            // Draw both leaves with opposite swing directions
            drawLeaf(point1, midPoint, 1); // Left leaf
            drawLeaf(point2, midPoint, -1); // Right leaf
        } else if (type === DoorType.DOORWAY || type === DoorType.PATIO) {
            const centerX = (point1.x + point2.x) / 2;
            const centerY = (point1.y + point2.y) / 2;

            const width = Math.hypot(point2.x - point1.x, point2.y - point1.y);
            const height = Math.min(width / 5, 20);

            const angle = Math.atan2(point2.y - point1.y, point2.x - point1.x);

            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate(angle);

            ctx.beginPath();
            ctx.globalAlpha = opacity;
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.strokeRect(-width / 2, -height / 2, width, height);

            ctx.restore();
        }
    }
    static drawsStaticLines(ctx, points, color, width = 2, scale = 1) {
        ctx.beginPath();
        ctx.moveTo(points[0].x / scale, points[0].y / scale);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x / scale, points[i].y / scale);
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.stroke();
    }

    static measureDistance = (point1, point2, scaleFactor) => {
        point1 = {
            x: point1.x,
            y: point1.y,
        };
        point2 = {
            x: point2.x,
            y: point2.y,
        };

        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const distanceInPixels = distance / INCH_TO_PX;
        const distanceInFeet = distanceInPixels / scaleFactor;

        // let feet = Math.floor(distanceInFeet);
        let inches = (distanceInFeet * 12).toFixed(2);

        if (inches === 12) {
            inches = 0;
        }

        return `${inches}`;
    };

    static lengthValidation(closetPolygon, scale) {
        for (let i = 0; i < closetPolygon.length; i++) {
            const distance = this.measureDistance(
                closetPolygon[i % closetPolygon.length],
                closetPolygon[(i + 1) % closetPolygon.length],
                scale,
            );
            if (distance > 500) {
                return true;
            }
        }
        return false;
    }

    static distanceInPixels = (point1, point2) => {
        point1 = {
            x: point1.x,
            y: point1.y,
        };
        point2 = {
            x: point2.x,
            y: point2.y,
        };

        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const distanceInPixels = distance;

        return distanceInPixels;
    };

    static calibratingScale = (point1, point2, ft, inches, scaleFactor) => {
        point1 = {
            x: point1.x,
            y: point1.y,
        };
        point2 = {
            x: point2.x,
            y: point2.y,
        };

        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (!distance) {
            return;
        }
        const distanceInPixels = distance / INCH_TO_PX;
        const distanceInFeet = distanceInPixels / scaleFactor;

        const feet = Math.floor(distanceInFeet);
        const inches2 = Math.round((distanceInFeet - feet) * 12);

        const actualDistanceInInches = Math.round(feet * 12 + inches2);

        const givenDistanceInInches = Math.round(ft * 12 + inches);

        return givenDistanceInInches / actualDistanceInInches;
    };

    static drawDistanceLabel = (
        ctx,
        point1,
        point2,
        scaleFactor,
        bColor = 'black',
        tColor = 'white',
    ) => {
        const distanceInPx = this.distanceInPixels(point1, point2);
        const distanceText = this.measureDistance(point1, point2, scaleFactor);
        const midpointX = (point1.x + point2.x) / 2;
        const midpointY = (point1.y + point2.y) / 2;
        let fontSize = 16;
        const maxHeight = 15;

        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let textMetrics = ctx.measureText(distanceText);
        let textWidth = textMetrics.width;
        let textHeight = fontSize;
        const percentage = 0.5;

        const targetWidth = distanceInPx * percentage;

        if (textWidth > targetWidth) {
            fontSize = (targetWidth / textWidth) * fontSize;
        } else if (textWidth < targetWidth) {
            fontSize = (targetWidth / textWidth) * fontSize;
        }

        fontSize = Math.min(fontSize, maxHeight);

        ctx.font = `${fontSize}px Arial`;
        textMetrics = ctx.measureText(distanceText);
        textWidth = textMetrics.width;
        textHeight = fontSize;

        let textOffset = Math.min(distanceInPx * 0.1, 8) + textHeight / 2;

        const padding = 0;
        const rectWidth = textWidth + 2 * padding;
        const rectHeight = Math.min(textHeight, maxHeight);
        const textY = midpointY + textOffset;
        if (rectWidth < 15) {
            return;
        }

        ctx.fillStyle = bColor;
        ctx.fillRect(
            midpointX - rectWidth / 2,
            midpointY + textOffset - rectHeight / 2,
            rectWidth,
            rectHeight,
        );

        ctx.fillStyle = tColor;
        ctx.fillText(distanceText, midpointX, textY);
    };

    static drawDistanceLabelForAttachments = (
        ctx,
        point1,
        point2,
        scaleFactor,
        bColor = '#ADD8E6',
        tColor = '#003366',
    ) => {
        const distanceInPx = this.distanceInPixels(point1, point2);
        const distanceText = this.measureDistance(point1, point2, scaleFactor);
        const midpointX = (point1.x + point2.x) / 2;
        const midpointY = (point1.y + point2.y) / 2;
        let fontSize = 16;
        const maxHeight = 15;

        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let textMetrics = ctx.measureText(distanceText);
        let textWidth = textMetrics.width;
        let textHeight = fontSize;
        const percentage = 0.5;

        const targetWidth = distanceInPx * percentage;

        if (textWidth > targetWidth) {
            fontSize = (targetWidth / textWidth) * fontSize;
        } else if (textWidth < targetWidth) {
            fontSize = (targetWidth / textWidth) * fontSize;
        }

        fontSize = Math.min(fontSize, maxHeight);

        ctx.font = `${fontSize}px Arial`;
        textMetrics = ctx.measureText(distanceText);
        textWidth = textMetrics.width;
        textHeight = fontSize;
        let textOffset = Math.min(distanceInPx * 0.1, 8) + textHeight / 2;

        const padding = 0;
        const rectWidth = textWidth + 2 * padding;
        const rectHeight = Math.min(textHeight, maxHeight);
        const textY = midpointY - textOffset;
        if (rectWidth < 15) {
            return;
        }

        ctx.fillStyle = bColor;
        ctx.fillRect(
            midpointX - rectWidth / 2,
            midpointY - textOffset - rectHeight / 2,
            rectWidth,
            rectHeight,
        );

        ctx.fillStyle = tColor;
        ctx.fillText(distanceText, midpointX, textY);
    };

    static findIntersection(line1, line2) {
        // Unpack points
        const { x: x1, y: y1 } = line1[0];
        const { x: x2, y: y2 } = line1[1];
        const { x: x3, y: y3 } = line2[0];
        const { x: x4, y: y4 } = line2[1];

        // Helper function to check if a value is within a range
        function isBetween(a, b, c) {
            return Math.min(a, b) <= c && c <= Math.max(a, b);
        }

        // Handle vertical line cases separately
        if (x1 === x2 && x3 === x4) {
            // Both lines are vertical
            if (x1 !== x3) return null; // Parallel vertical lines, no intersection

            // Check for overlapping range in y-coordinates
            if (isBetween(y3, y4, y1) || isBetween(y3, y4, y2)) {
                return {
                    x: x1,
                    y: Math.max(Math.min(y1, y2), Math.min(y3, y4)),
                };
            }
            return null; // No overlap
        }

        if (x1 === x2) {
            // Line1 is vertical, find intersection with Line2
            const m2 = (y4 - y3) / (x4 - x3);
            const y = m2 * (x1 - x3) + y3;

            if (isBetween(x3, x4, x1)) {
                return { x: x1, y };
            }
            return null;
        }

        if (x3 === x4) {
            // Line2 is vertical, find intersection with Line1
            const m1 = (y2 - y1) / (x2 - x1);
            const y = m1 * (x3 - x1) + y1;

            if (isBetween(y3, y4, y)) {
                return { x: x3, y };
            }
            return null;
        }

        // Calculate slopes
        const m1 = (y2 - y1) / (x2 - x1);
        const m2 = (y4 - y3) / (x4 - x3);

        // Parallel but not coincident lines
        if (m1 === m2) return null;

        // Intersection point calculation
        const x = (y1 - y3 + m2 * x3 - m1 * x1) / (m2 - m1);
        const y = m1 * (x - x1) + y1;

        if (isBetween(x3, x4, x) && isBetween(y3, y4, y)) {
            return { x, y };
        }

        return null;
    }

    static renderPageCanvas = async (pdfDoc, pageNumber) => {
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min(181 / viewport.width, 128 / viewport.height);
        const scaledViewport = page.getViewport({ scale });

        let canvas = document.createElement('canvas');
        canvas.width = 181;
        canvas.height = 128;
        let context = canvas.getContext('2d');
        const offsetX = (181 - scaledViewport.width) / 2;
        const offsetY = (128 - scaledViewport.height) / 2;
        context.setTransform(1, 0, 0, 1, offsetX, offsetY);
        await page.render({
            canvasContext: context,
            viewport: scaledViewport,
        }).promise;

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const imageUrl = URL.createObjectURL(blob);
                        // viewManager.setImageUrlinUiPages(pageNum, imageUrl);
                        resolve({ imageUrl, pageNumber });
                        // Clean up canvas resources
                        context.clearRect(0, 0, canvas.width, canvas.height);
                        canvas.remove(); // Remove canvas from DOM
                        page.cleanup();
                        canvas.width = 0; // Clear canvas width
                        canvas.height = 0; // Clear canvas height
                        context = null; // Clear context reference
                        canvas = null; // Clear canvas reference
                    }
                },
                'image/webp',
                0.5,
            );
        });
    };
}
